import moment from "moment"
import { BLACKOUT_SCOPES } from "helpers/blackouts"

/** `booked_date` comes back US-style, which `moment` would otherwise read as a locale guess. */
const API_DATE_FORMAT = "MM/DD/YYYY"

/**
 * A multi-day booking has no end time, and the API says so by repeating the start.
 * See the multi-day rule: the end is derived from the duration, never sent.
 */
const toTimeRange = ({ start_time, end_time }) =>
  start_time && end_time && start_time !== end_time ? { start_time, end_time } : { start_time: null, end_time: null }

/** "Boat for multiday - Asset #0002" — an asset is only identifiable with its number. */
const formatAssetLabel = (item) =>
  [item?.boat_name, item?.asset_number && `Asset #${item.asset_number}`].filter(Boolean).join(" - ")

const toCrewResource = (item) => ({
  id: item?.profile_id,
  label: item?.crew_name || "",
  category: Array.isArray(item?.roles) ? item.roles.join(", ") : ""
})

/**
 * The id is the **asset**, not the boat: a blackout strands one numbered asset, and the booking is
 * handed to another asset, so the boat id would not say which one.
 *
 * No category — the boat is already the first half of the label, and the sheet would print it
 * twice ("Boat 1 - Asset #0002 (Boat 1)").
 */
const toEquipmentResource = (item) => ({
  id: item?.asset_id,
  label: formatAssetLabel(item),
  category: ""
})

/**
 * Normalizes `POST /guide/blackouts/:id/check_impact` into the rows the Replace sheet renders.
 *
 * One row per **stranded resource**, not per booking: a crew blackout can cover two people on the
 * same trip, and each needs its own replacement — the backend keeps the booking in conflict until
 * every one is replaced. Rows of the same booking share its `booking_id`, so a row is keyed on the
 * pair.
 *
 * The response carries its alternatives **per booking**, not once for the whole blackout — a crew
 * member free on Tuesday is not necessarily free on Thursday — so candidates belong to the row,
 * and a booking with an empty list is a real answer, not a loading state.
 *
 * Crew and equipment differ in every field name, hence the two mappers; everything after this
 * point is scope-agnostic.
 *
 * @param {Object} payload the raw `check_impact` body
 * @param {string} scope `BLACKOUT_SCOPES.CREW` or `BLACKOUT_SCOPES.EQUIPMENT`
 * @returns {{rows: Object[], requiresSubstitution: boolean}}
 */
export const normalizeImpact = (payload, scope) => {
  const isEquipment = (payload?.blackout_scope || scope) === BLACKOUT_SCOPES.EQUIPMENT
  const toResource = isEquipment ? toEquipmentResource : toCrewResource
  const affectedKey = isEquipment ? "affected_equipment" : "affected_crew"
  const affected = Array.isArray(payload?.affected_bookings) ? payload.affected_bookings : []

  return {
    requiresSubstitution: Boolean(payload?.requires_substitution),
    rows: affected.flatMap((booking) => {
      const bookedDate = booking?.booked_date ? moment(booking.booked_date, API_DATE_FORMAT) : null
      const bookingFields = {
        uuid: booking?.booking_uuid,
        booking_id: booking?.booking_id,
        trip_title: booking?.experience_name || "",
        client_name: booking?.client_name || "",
        start_date: bookedDate?.isValid() ? bookedDate.format("YYYY-MM-DD") : null,
        ...toTimeRange(booking || {})
      }
      const candidates = (booking?.available_alternatives || []).map((candidate) => ({
        ...toResource(candidate),
        // Crew alternatives carry how busy they already are; equipment does not.
        upcoming_bookings_count: candidate?.upcoming_bookings_count,
        is_available: true
      }))

      return (booking?.[affectedKey] || []).map((affectedResource) => {
        const resource = toResource(affectedResource)
        return { ...bookingFields, id: `${booking?.booking_id}:${resource.id}`, resource, candidates }
      })
    })
  }
}
